import * as React from 'react';
import {
  addDemandActivity,
  getDemand,
  listDemandActivities,
  transitionDemand,
  type Demand,
  type DemandActivity,
  type DemandStage,
} from '../lib/demands-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { useT } from '../lib/i18n.js';
import { CalendarEventForm } from './calendar-event-form.js';
import { listCalendarEventTypes, type CalendarEventType } from '../lib/calendar-api.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { MatchesPanel } from './matches-panel.js';
import { DemandQualificationPanel, SimilarListingsPanel } from './demand-ai-panels.js';

export interface DemandDrawerProps {
  readonly demandId: string;
  readonly stages: readonly DemandStage[];
  readonly onClose: () => void;
  readonly onChanged: (demand: Demand) => void;
}

const ACTIVITY_KINDS = ['note', 'email', 'phone', 'meeting'] as const;
type ActivityKind = (typeof ACTIVITY_KINDS)[number];

function money(value: string | null, currency: string): string | null {
  if (value === null || value === '') return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `${value} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

/**
 * Detail karty (krok CRM1, bod 2) — vzorem `DealDrawer` z crm-web: hlavička,
 * co hledá, kontakt, další krok a ČASOVÁ OSA AKTIVIT. Přesun mezi sloupci
 * se tady dělá tlačítky nad dostupnými cíli, které čtou `allowedTransitions`
 * z API — žádný „tahni kam chceš“ vedle si necháváme.
 */
/** Krok CRM4: výměra „50–70 m²" / „od 50 m²" / „do 70 m²" (hodnoty jsou numeric řetězce). */
function areaRange(from: string | null | undefined, to: string | null | undefined): string | null {
  // `undefined` = poptávka z API starší než CRM4 (pole v odpovědi chybí).
  const clean = (v: string | null | undefined): string | null =>
    v === null || v === undefined ? null : v.replace(/\.0+$/, '');
  const a = clean(from);
  const b = clean(to);
  if (a !== null && b !== null) return `${a}–${b} m²`;
  if (a !== null) return `≥ ${a} m²`;
  if (b !== null) return `≤ ${b} m²`;
  return null;
}

export function DemandDrawer({ demandId, stages, onClose, onChanged }: DemandDrawerProps): React.JSX.Element {
  const t = useT();
  const [demand, setDemand] = React.useState<Demand | null>(null);
  const [activities, setActivities] = React.useState<readonly DemandActivity[]>([]);
  const [note, setNote] = React.useState('');
  const [kind, setKind] = React.useState<ActivityKind>('note');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      setDemand(await getDemand(demandId));
    } catch (e) {
      setDemand(null);
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.drawerLoadFailed'));
    }
    try {
      setActivities(await listDemandActivities(demandId));
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.drawerActivitiesFailed'));
    }
  }, [demandId, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<Demand>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const updated = await fn();
      setDemand(updated);
      onChanged(updated);
      setActivities(await listDemandActivities(demandId));
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.drawerSaveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function addActivity(): Promise<void> {
    if (note.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await addDemandActivity(demandId, kind, note.trim());
      setNote('');
      setActivities(await listDemandActivities(demandId));
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.drawerActivityFailed'));
    } finally {
      setBusy(false);
    }
  }

  // Krok CRM3 (zadani bod 2): „vytvoreni z karty poptavky". Formulář
  // události se otevře uvnitř detailu poptávky s PŘEDVYPLNĚNOU vazbou
  // `demandId` — protože kliknout „prohlídka“ u poptávky znamená
  // naplánovat prohlídku TEHOTTO poptávky, ne nějaké jiné.
  const [showEvent, setShowEvent] = React.useState(false);
  const [eventTypes, setEventTypes] = React.useState<CalendarEventType[]>([]);
  const [members, setMembers] = React.useState<TenantMember[]>([]);

  React.useEffect(() => {
    if (!showEvent) return;
    let cancelled = false;
    void Promise.all([listCalendarEventTypes(), listMembers()])
      .then(([types, list]) => {
        if (cancelled) return;
        setEventTypes(types);
        setMembers(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [showEvent]);

  const stageLabelByKey = React.useMemo(
    () => new Map(stages.map((stage) => [stage.key, stage.label])),
    [stages],
  );
  const moves = demand === null ? [] : demand.allowedTransitions.filter((key) => stageLabelByKey.has(key));

  return (
    <aside className="rh-demand-drawer" data-testid="demand-drawer" aria-label={t('webAdmin.demands.drawer')}>
      <header className="rh-demand-drawer__header">
        <h2 className="rh-kanban-card__title">{demand?.title ?? t('webAdmin.demands.drawer')}</h2>
        <button type="button" className="rh-btn" onClick={onClose} data-testid="demand-drawer-close">
          {t('webAdmin.demands.drawerClose')}
        </button>
      </header>

      {error !== null && (
        <p role="alert" className="rh-demand-drawer__error" data-testid="demand-drawer-error">
          {error}
        </p>
      )}

      {demand !== null && (
        <section className="rh-demand-drawer__section" data-testid="demand-drawer-detail">
          <h3>{t('webAdmin.demands.drawerWants')}</h3>
          <dl className="rh-demand-drawer__grid">
            <dt>{t('webAdmin.demands.drawerKind')}</dt>
            <dd>{demand.wantedKind ?? '—'}</dd>
            <dt>{t('webAdmin.demands.drawerLocality')}</dt>
            <dd>{demand.wantedLocality ?? '—'}</dd>
            <dt>{t('webAdmin.demands.drawerPrice')}</dt>
            <dd>{money(demand.wantedPriceTo, demand.wantedCurrency) ?? '—'}</dd>
            <dt>{t('webAdmin.demands.drawerDisposition')}</dt>
            <dd>{demand.wantedDisposition ?? '—'}</dd>
            <dt>{t('webAdmin.demands.drawerArea')}</dt>
            <dd data-testid="demand-drawer-area">{areaRange(demand.wantedAreaFrom, demand.wantedAreaTo) ?? '—'}</dd>
          </dl>
        </section>
      )}

      {/* Krok NAP4 bod A9: vážnost zájemce ze signálů, které zadá makléř.
          Pomocný ukazatel s důvody — nic nezamyká a poptávku nezavírá. */}
      {demand !== null && (
        <section className="rh-demand-drawer__section" data-testid="demand-drawer-qualification">
          <h3>{t('webAdmin.demands.qualification.heading')}</h3>
          <DemandQualificationPanel demandId={demand.id} onScored={() => void load()} />
        </section>
      )}

      {/* Krok CRM4: vhodné nemovitosti k poptávce (párování, server počítá skóre i vysvětlení). */}
      {demand !== null && (
        <section className="rh-demand-drawer__section" data-testid="demand-drawer-matches">
          <h3>{t('webAdmin.matches.demandHeading')}</h3>
          <MatchesPanel demandId={demand.id} />
        </section>
      )}

      {/* Krok NAP4 bod A9: podobné nabídky podle volného textu přání — vedle
          filtru shod výše, ne místo něj. */}
      {demand !== null && (
        <section className="rh-demand-drawer__section" data-testid="demand-drawer-similar">
          <h3>{t('webAdmin.matches.similarHeading')}</h3>
          <SimilarListingsPanel demandId={demand.id} />
        </section>
      )}

      {demand !== null && (
        <section className="rh-demand-drawer__section">
          <h3>{t('webAdmin.demands.drawerContact')}</h3>
          <dl className="rh-demand-drawer__grid">
            <dt>{t('webAdmin.demands.importName')}</dt>
            <dd data-testid="demand-drawer-name">{demand.contactName ?? t('webAdmin.demands.noContact')}</dd>
            <dt>{t('webAdmin.demands.importEmail')}</dt>
            <dd>{demand.contactEmail ?? '—'}</dd>
            <dt>{t('webAdmin.demands.importPhone')}</dt>
            <dd>{demand.contactPhone ?? '—'}</dd>
            <dt>{t('webAdmin.demands.drawerSource')}</dt>
            <dd data-testid="demand-drawer-source">
              {t(`crm.source.${demand.source}` as 'crm.source.portal')}
              {demand.sourcePortal !== null ? ` · ${demand.sourcePortal}` : ''}
            </dd>
            <dt>{t('webAdmin.demands.drawerNextStep')}</dt>
            <dd data-testid="demand-drawer-next-step">
              {demand.nextStep ?? t('webAdmin.demands.drawerNoNextStep')}
            </dd>
          </dl>
        </section>
      )}

      {demand !== null && (
        <section className="rh-demand-drawer__section">
          <h3>{t('webAdmin.demands.stageMove')}</h3>
          {moves.length === 0 ? (
            <p data-testid="demand-drawer-no-moves">{t('webAdmin.demands.noMoveAvailable')}</p>
          ) : (
            <div className="rh-demand-drawer__moves" data-testid="demand-drawer-moves">
              {moves.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="rh-btn"
                  disabled={busy}
                  data-testid={`demand-move-${key}`}
                  onClick={() => void run(() => transitionDemand(demand.id, key))}
                >
                  {stageLabelByKey.get(key)}
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="rh-demand-drawer__section">
        <h3>{t('webAdmin.demands.drawerActivities')}</h3>
        <ul className="rh-demand-drawer__timeline" data-testid="demand-activities">
          {activities.map((activity) => (
            <li key={activity.id} data-testid={`demand-activity-${activity.id}`}>
              <span className="rh-badge rh-badge--neutral">
                {t(`webAdmin.demands.activity.${activity.kind}` as 'webAdmin.demands.activity.note')}
              </span>
              <span>
                {activity.summary !== null && activity.summary !== '' ? activity.summary : ''}
                {activity.fromStage !== null && activity.toStage !== null
                  ? `${stageLabelByKey.get(activity.fromStage) ?? activity.fromStage} → ${stageLabelByKey.get(activity.toStage) ?? activity.toStage}`
                  : ''}
                {activity.body !== null && activity.body !== '' ? ` ${activity.body}` : ''}
              </span>
            </li>
          ))}
        </ul>
        <div className="rh-demand-drawer__compose">
          <label className="rh-demand-drawer__compose-kind">
            {t('webAdmin.demands.drawerSource')}
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ActivityKind)}
              data-testid="demand-activity-kind"
            >
              {ACTIVITY_KINDS.map((option) => (
                <option key={option} value={option}>
                  {t(`webAdmin.demands.activity.${option}` as 'webAdmin.demands.activity.note')}
                </option>
              ))}
            </select>
          </label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('webAdmin.demands.drawerActivityPlaceholder')}
            aria-label={t('webAdmin.demands.drawerActivityPlaceholder')}
            data-testid="demand-activity-input"
          />
          <button
            type="button"
            className="rh-btn rh-btn--primary"
            disabled={busy || note.trim() === ''}
            data-testid="demand-activity-add"
            onClick={() => void addActivity()}
          >
            {t('webAdmin.demands.drawerActivityAdd')}
          </button>
        </div>
      </section>
      {demand !== null && (
        <section className="rh-demand-drawer__section" data-testid="demand-drawer-calendar">
          <h3>{t('webAdmin.calendar.drawer.heading')}</h3>
          {!showEvent && (
            <button
              type="button"
              className="rh-btn"
              data-testid="demand-drawer-new-event"
              onClick={() => setShowEvent(true)}
            >
              {t('webAdmin.demands.drawerNewEvent')}
            </button>
          )}
          {showEvent && eventTypes.length > 0 && (
            <CalendarEventForm
              types={eventTypes}
              members={members}
              seed={{ demandId: demand.id, partyId: demand.partyId, eventType: 'viewing' }}
              submitLabel={t('webAdmin.demands.drawerNewEvent')}
              onCreated={() => setShowEvent(false)}
              onCancel={() => setShowEvent(false)}
            />
          )}
        </section>
      )}
    </aside>
  );
}
